import { StatusModal } from '@/components/StatusModal';
import { RichTextEditor } from '@/components/vyroba/RichText';
import type { VykazData, VykazRow } from '@/components/vyroba/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { downloadVykazPdf } from '@/lib/vykazPdf';
import { useEffect, useState } from 'react';

const MONTHS = [
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
];

/** Sekundy → „1 hod, 43 min 46 sec" (tvar, na který je dílna zvyklá). */
export function fmtDur(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h} hod, ${m} min ${s} sec` : `${m} min ${s} sec`;
}

/** UTC z Tabidoo → „04.05.2026 06:04:34" v našem čase. */
export function fmtDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Podbarvení výkonu — zelená / oranžová / červená jako ve starém výkazu. */
export function perfClass(pct: number): string {
  if (pct >= 75) return 'bg-success-light text-text';
  if (pct >= 50) return 'bg-warning-light text-text';
  return 'bg-danger-light text-text';
}

const SELECT =
  'rounded-[5px] border border-[#cbd5e1] bg-white px-3 py-2 text-sm font-semibold text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer';

/** Kusy po kategoriích — podklad pro rozbalovací tabulku i PDF. */
export function byCategory(rows: VykazRow[]): { category: string; qty: number }[] {
  const map = new Map<string, number>();
  for (const r of rows) map.set(r.category || '—', (map.get(r.category || '—') ?? 0) + r.total_qty);
  return [...map].map(([category, qty]) => ({ category, qty }));
}

/**
 * Výkazy šicí dílny — měsíční přehled dokončených zakázek jedné švadlenky.
 * Čísla se počítají ze zakázek, poznámky a počet chyb zadává vedoucí.
 * „PDF výkaz" otevře tiskovou sestavu (tisk do PDF přes prohlížeč).
 */
export function VykazyPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [svadlenka, setSvadlenka] = useState('');
  const [svadlenky, setSvadlenky] = useState<string[]>([]);
  const [data, setData] = useState<VykazData | null>(null);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState('0');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  useEffect(() => {
    apiFetch<{ svadlenky: string[] }>('/api/vyroba/vykazy/svadlenky')
      .then((d) => {
        setSvadlenky(d.svadlenky);
        setSvadlenka((prev) => prev || (d.svadlenky[0] ?? ''));
      })
      .catch(() => setSvadlenky([]));
  }, []);

  async function load() {
    if (!svadlenka) return;
    setLoading(true);
    setData(null);
    try {
      const d = await apiFetch<VykazData>(
        `/api/vyroba/vykazy?year=${year}&month=${month}&svadlenka=${encodeURIComponent(svadlenka)}`,
      );
      setData(d);
      setNote(d.note);
      setErrors(String(d.errors));
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Načtení selhalo', kind: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    try {
      await apiFetch('/api/vyroba/vykazy', {
        method: 'PUT',
        body: {
          year,
          month,
          svadlenka,
          errors: Number.parseInt(errors, 10) || 0,
          note,
        },
      });
      setToast({ msg: 'Uloženo', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Uložení selhalo', kind: 'error' });
    }
  }

  // Rovnou stažené PDF; náhled sestavy je v novém panelu (odkaz vedle).
  async function savePdf() {
    if (!data) return;
    setSaving(true);
    try {
      await downloadVykazPdf({
        data,
        note,
        errors: Number.parseInt(errors, 10) || 0,
        svadlenka,
        month,
        year,
      });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof Error ? e.message : 'PDF se nepodařilo vytvořit',
        kind: 'error',
      });
    } finally {
      setSaving(false);
    }
  }

  function openReport() {
    const q = new URLSearchParams({
      year: String(year),
      month: String(month),
      svadlenka,
    });
    window.open(`/vyroba/sici-dilna/vykazy/tisk?${q.toString()}`, '_blank', 'noopener');
  }

  const cats = data ? byCategory(data.rows) : [];

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Výkazy</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Přehled dokončených zakázek s rozdělením na švadlenky.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={openReport}
            disabled={!data || data.rows.length === 0}
            className="text-sm font-semibold text-text-secondary hover:text-text underline disabled:opacity-50 cursor-pointer"
          >
            Náhled
          </button>
          <button
            type="button"
            onClick={savePdf}
            disabled={!data || data.rows.length === 0 || saving}
            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-[17px] font-bold rounded-lg px-8 py-3.5 cursor-pointer transition-colors"
          >
            {saving ? 'Připravuji…' : '💾 Uložit PDF'}
          </button>
        </div>
      </div>

      {/* Filtr */}
      <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="block text-sm font-semibold text-text mb-1">Rok</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={SELECT}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-semibold text-text mb-1">Měsíc</span>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className={SELECT}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-semibold text-text mb-1">Švadlenka</span>
          <select
            value={svadlenka}
            onChange={(e) => setSvadlenka(e.target.value)}
            className={SELECT}
          >
            {svadlenky.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={load}
          disabled={loading || !svadlenka}
          className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-6 py-2.5 cursor-pointer transition-colors"
        >
          {loading ? 'Načítám…' : 'Filtrovat'}
        </button>
      </div>

      {data && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Souhrn */}
            <div className="bg-surface rounded-xl shadow-card px-6 py-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[15px]">
                <Stat label="Počet zakázek" value={String(data.summary.orders)} />
                <Stat label="Časová náročnost" value={data.summary.norm_hours.toFixed(1)} />
                <Stat label="Doba šití" value={data.summary.sewing_hours.toFixed(1)} />
                <Stat label="Celkový počet kusů" value={String(data.summary.total_qty)} />
                <label className="flex items-center gap-2">
                  <span className="font-bold text-text">Počet chyb:</span>
                  <input
                    type="number"
                    value={errors}
                    onChange={(e) => setErrors(e.target.value)}
                    className="w-20 rounded-[5px] border border-[#cbd5e1] bg-white px-2 py-1 text-sm font-semibold text-text focus:border-primary focus:outline-none"
                  />
                </label>
                <Stat label="Chybovost" value={`${data.summary.error_rate.toFixed(1)} %`} />
              </div>
            </div>

            {/* Poznámky */}
            <div className="bg-surface rounded-xl shadow-card px-6 py-5">
              <div className="text-sm font-bold text-text mb-2">Poznámky</div>
              <RichTextEditor value={note} onChange={setNote} rows={8} />
              <button
                type="button"
                onClick={save}
                className="mt-3 bg-action hover:bg-action-dark text-white text-sm font-bold rounded-lg px-6 py-2.5 cursor-pointer transition-colors"
              >
                Uložit poznámky
              </button>
            </div>
          </div>

          {/* Kusy dle kategorie */}
          <div className="mt-5 bg-surface rounded-xl shadow-card px-6 py-4">
            <button
              type="button"
              onClick={() => setShowCats((v) => !v)}
              className="flex items-center gap-3 text-sm font-bold text-text cursor-pointer"
            >
              Počet kusů dle kategorie
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-action text-white text-lg leading-none">
                {showCats ? '−' : '+'}
              </span>
            </button>
            {showCats && (
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-text-secondary bg-bg">
                    <th className="px-3 py-2 rounded-l-md">Kategorie</th>
                    <th className="px-3 py-2 text-right rounded-r-md">Celkem</th>
                  </tr>
                </thead>
                <tbody>
                  {cats.map((c) => (
                    <tr key={c.category} className="border-b border-border-light">
                      <td className="px-3 py-1.5 text-text">{c.category}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-text">{c.qty}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-3 py-2 font-bold text-text">Celkem</td>
                    <td className="px-3 py-2 text-right font-bold text-text">
                      {data.summary.total_qty}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Zakázky */}
          <div className="mt-5 bg-surface rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-action text-white text-left text-xs font-bold">
                  <th className="px-3 py-2.5">ID zakázky</th>
                  <th className="px-3 py-2.5">Kód produktu</th>
                  <th className="px-3 py-2.5 text-right">Celkem ks</th>
                  <th className="px-3 py-2.5">Švadlenka</th>
                  <th className="px-3 py-2.5">Start šití</th>
                  <th className="px-3 py-2.5">Konec šití</th>
                  <th className="px-3 py-2.5">Předpokládaný čas šití</th>
                  <th className="px-3 py-2.5">Doba šití</th>
                  <th className="px-3 py-2.5 text-right">Výkon</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-text-muted">
                      V tomhle měsíci nemá {svadlenka} žádnou dokončenou zakázku.
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r) => (
                    <tr key={r.id_zakazky} className="border-b border-border-light">
                      <td className="px-3 py-1.5 font-semibold text-text">{r.id_zakazky}</td>
                      <td className="px-3 py-1.5 font-semibold text-text">{r.product_code}</td>
                      <td className="px-3 py-1.5 text-right text-text">{r.total_qty}</td>
                      <td className="px-3 py-1.5 text-text-secondary">{r.svadlenka}</td>
                      <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">
                        {fmtDateTime(r.start)}
                      </td>
                      <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">
                        {fmtDateTime(r.konec)}
                      </td>
                      <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">
                        {r.norm_text}
                      </td>
                      <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">
                        {fmtDur(r.duration_sec)}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-semibold whitespace-nowrap ${perfClass(r.performance)}`}
                      >
                        {r.performance.toFixed(2)}%
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-bold text-text">{label}:</span>{' '}
      <span className="text-text-secondary">{value}</span>
    </div>
  );
}
